import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { ContextProvider } from '../../contexts/ContextProvider';

import Connexion from '../screens/Connexion';
import Diagnostic from '../screens/Diagnostic';
import SasInstallation from '../screens/SasInstallation';

import { useLangueMobile } from '../i18n/useLangueMobile';

import ApplicationMobile from './ApplicationMobile';

/** Hors registre, volontairement — voir l'en-tête de `Diagnostic.tsx`. */
export const CHEMIN_DIAGNOSTIC = '/diagnostic';

/** La clé que `ContextProvider` relit à son démarrage pour hydrater la sélection. */
const CLE_ETABLISSEMENT = 'SELECTED_FACILITY_ID';

/**
 * Adopter l'établissement porté par le lien de la notification — #77.
 *
 * Le `navigate` du payload déclaratif vaut
 * `{mobile_url}/messaging/{customer_id}?facility_id={id}` : il PORTE l'établissement
 * parce qu'une sélection peut être un groupe (#25), et que l'app ouverte par une
 * notification ne peut donc pas déduire l'hôtel de son état courant. Sans cette
 * adoption, le fil partirait chercher `/customer-basics` avec le `X-Facility-Id`
 * de la sélection précédente — la mauvaise conversation, ou un 403.
 *
 * **Trois lignes plutôt qu'un hook, parce que le chemin existe déjà.**
 * `ContextProvider` lit cette clé à son démarrage, appelle `/facilities/{id}` et
 * retombe sur la facility du compte si l'accès est refusé. On n'ajoute donc ni
 * requête, ni validation, ni état — et un `facility_id` forgé dans l'URL est
 * arbitré par le 403 du serveur, jamais par le client.
 *
 * **La place compte autant que le code** : appelée ici, au-dessus du provider, la
 * valeur est en place AVANT le premier rendu. Un `useEffect` à l'intérieur
 * corrigerait la sélection après coup, sous le pouce de quelqu'un qui a déjà
 * commencé à viser — exactement ce que #27 refuse.
 */
let lienAdopte = false;

function adopterEtablissementDuLien(): void {
    // UNE SEULE FOIS par chargement. `Shell` se rend à nouveau quand `standalone`
    // change, et l'URL garde son `?facility_id` : sans ce verrou, une bascule
    // d'établissement faite depuis le panneau serait réécrasée par le lien qui a
    // ouvert l'app — la sélection de l'utilisateur perdrait contre une notification
    // qu'il a déjà lue.
    if (lienAdopte) return;
    lienAdopte = true;

    try {
        const demande = new URLSearchParams(window.location.search).get('facility_id');
        // `Number.isInteger` plutôt qu'une vérité JavaScript : `'0'` et `'abc'`
        // doivent tous deux laisser la sélection en place, pas l'écraser.
        const id = Number(demande);
        if (demande && Number.isInteger(id) && id > 0) {
            localStorage.setItem(CLE_ETABLISSEMENT, String(id));
        }
    } catch {
        /* Stockage indisponible : la sélection en place reste la bonne réponse. */
    }
}

/**
 * La coque : trois portes, dans cet ordre.
 *
 * **1. `/diagnostic`, avant tout le reste.** Ni porte standalone, ni fournisseurs,
 * ni routeur — un diagnostic qui dépend de ce qu'il diagnostique ne prouve rien.
 * D'où la lecture directe de `window.location`, qui précède volontairement le
 * routeur. Hors registre, donc absent du panneau et jamais compté dans les seuils
 * 0/1/≥2 (voir `mobileScreens.ts`).
 *
 * **2. Hors standalone, le sas.** #21 : `m.` dans un onglet ne montre aucun
 * formulaire de connexion. Un `m.` pleinement utilisable produirait deux sessions
 * par appareil et des utilisateurs convaincus d'avoir l'app tout en étant
 * injoignables.
 *
 * **3. Sans jeton, la connexion.** `ContextProvider` n'est monté QUE si un jeton
 * existe : sa première action, sans jeton, est un `redirectToLogin()` qui vise
 * `/login/m.trigger-flow.com` — `getDomainSlug()` rend le hostname sur cette
 * origine — c'est-à-dire un chemin que le repli SPA sert avec `mobile.html`, qui
 * n'a pas cette route. Un écran blanc. D'où la connexion montée ici, au-dessus des
 * fournisseurs, et non derrière eux (#79).
 *
 * Le lot 0 montait `Shell` nu, sans aucun fournisseur, parce que la coque devait
 * s'afficher pour un visiteur anonyme. C'est toujours vrai des portes 1 et 2 —
 * elles restent au-dessus des fournisseurs.
 */
export default function Shell() {
    const [standalone, setStandalone] = useState(
        () => window.matchMedia('(display-mode: standalone)').matches,
    );
    const [token] = useState(() => localStorage.getItem('ACCESS_TOKEN'));
    // Changer de langue remonte l'application à neuf : `t()` reste une fonction
    // simple, sans abonnement dans chaque écran (voir `i18n/langue.ts`).
    const langue = useLangueMobile();

    useEffect(() => {
        const requete = window.matchMedia('(display-mode: standalone)');
        setStandalone(requete.matches);

        const surChangement = (e: MediaQueryListEvent) => setStandalone(e.matches);
        requete.addEventListener('change', surChangement);
        return () => requete.removeEventListener('change', surChangement);
    }, []);

    if (window.location.pathname === CHEMIN_DIAGNOSTIC) return <Diagnostic />;

    // Avant les deux portes suivantes : le sas et l'écran de session absente
    // rendent le lien inaccessible, mais ils ne doivent pas le faire OUBLIER —
    // #79 fera revenir l'utilisateur ici une fois installé et connecté.
    adopterEtablissementDuLien();

    if (!standalone) return <SasInstallation />;

    // La déconnexion ne repasse PAS par cette ligne : `setToken(null)` vit dans
    // `ContextProvider`, sous cette coque, et n'y provoque aucun rendu. Elle prend
    // le chemin long — le provider voit son jeton disparaître, appelle
    // `redirectToLogin()`, le repli SPA sert `mobile.html`, et c'est le nouveau
    // chargement qui atterrit ici. Même chemin qu'une session expirée, ce qui est
    // exactement ce qu'on veut : un seul comportement à connaître.
    //
    // La connexion vit ICI, au-dessus des fournisseurs, et c'est le SEUL endroit du
    // produit où `m.` la propose (#21). C'est ce qui garantit une session par
    // appareil : le sas la refuse hors standalone, donc personne ne peut en ouvrir
    // une seconde dans un onglet.
    //
    // Un rechargement complet plutôt qu'un `setToken` : `ContextProvider` n'est pas
    // encore monté à cet endroit — c'est toute la règle de la coque — et il lit son
    // jeton au montage. Le recharger est le chemin le plus court vers un état
    // cohérent, et c'est déjà celui qu'empruntent la déconnexion et l'expiration.
    //
    // ⚠️ C'EST AUSSI CE QUI FAIT SURVIVRE LE LIEN PROFOND à la connexion (#20).
    //
    // `reload()` recharge l'URL COURANTE : le chemin `/messaging/42` et son
    // `?facility_id=` sont donc préservés sans qu'on ait rien à mémoriser, et
    // `adopterEtablissementDuLien` se rejoue au chargement suivant. Le veilleur de
    // nuit qui n'a pas ouvert l'app depuis trois semaines tape sa notification,
    // se connecte, et atterrit sur la conversation — pas sur l'accueil.
    //
    // Le `?redirect=` de #18 n'est PAS sur ce chemin : il sert la redirection du
    // web vers son écran de connexion, qui change d'URL. Ici on ne change pas
    // d'URL, donc il n'y a rien à mémoriser. Remplacer ce `reload()` par un
    // `setToken` casserait le lien profond sans casser aucun test de connexion —
    // d'où celui de `lienProfond.test.tsx` qui couvre le parcours entier.
    if (!token) {
        return (
            <Connexion
                onConnecte={(jeton) => {
                    localStorage.setItem('ACCESS_TOKEN', jeton);
                    window.location.reload();
                }}
            />
        );
    }

    return (
        <ContextProvider>
            <BrowserRouter>
                <ApplicationMobile key={langue} />
            </BrowserRouter>
        </ContextProvider>
    );
}

